import { useEffect, useState } from 'react';
import { useTexts } from 'utils/useTexts';
import { Button } from '@components/shared/Button/Button';
import { AssetLinks } from '@assets/assets';
import { useFormContext } from '@context/FormContext';
import useForm, { validate } from '@shared/hooks/useForm';
import { TextInput } from '@components/shared/form/TextInput';
import CustomSelect from '@components/shared/form/CustomSelectInput';
import { countryStates } from '@shared/constants/countryStates';
import { addressTypeOptions } from '@shared/constants/addressTypes';
import { AddressInfo, CDX_ACTIONS, UpdateAddressInfo } from 'types/form';
import { Alert } from '@components/shared/Alert/Alert';
import { convertMonthsToYearsAndMonths, monthsBetween, todaysDate } from 'utils/dateUtils';
import { PriceInput } from '@components/shared/form/PriceTextInput';
import { createAddressHistory } from '@services/addressHistory';
import useEffectOnce from '@shared/hooks/useEffectOnce';
import { useAnalytics } from '@shared/hooks/useAnalytics';
import { getContactData, REQUIRED_MONTHS } from '@services/utils';
import { SEGMENT_SCREEN_PARAMS } from '@shared/constants/segmentConstants';
import { convertArrayToSnakeCase } from 'utils/stringFormat';
import { useScreenNavigator } from '@shared/hooks/useScreenNavigator';

export type ICurrentAddressInfo = Omit<UpdateAddressInfo, 'county' | 'isCurrent' | 'monthsAtAddress'> & {
  moveInDate: string;
  moveOutDate: string;
};

type CurrentORUpdatedAddress = ICurrentAddressInfo | AddressInfo;

export const addOrEditAddress = (address: CurrentORUpdatedAddress): ICurrentAddressInfo => {
  return {
    streetLine1: address?.streetLine1,
    streetLine2: address?.streetLine2 ?? '',
    city: address?.city,
    state: address?.state,
    zip: address?.zip,
    moveInDate: 'moveInDate' in address ? address.moveInDate : '',
    moveOutDate: 'moveOutDate' in address ? address.moveOutDate : '',
    type: address?.type,
    monthlyPayment: address?.monthlyPayment,
  };
};

const TwoYearsAddressHistoryForm = () => {
  const { t } = useTexts();
  const { next } = useScreenNavigator();
  const { trackFormSubmit, trackPage, startInteraction } = useAnalytics({
    ...SEGMENT_SCREEN_PARAMS.ADDRESS_LOOP,
    field_count: 8,
    required_field_count: 6,
  });

  const { currentStep, addressHistory, dispatch, submitStart, submitSuccess, isLoading } = useFormContext();
  const [noOfMonths, setnoOfMonths] = useState<number | null>(null);

  const [addresses, setAddresses] = useState<ICurrentAddressInfo[]>([]);

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [noOfNewAddressAddedFromHistory, setnoOfNewAddressAddedFromHistory] = useState<number>(0);
  const [isShowDuplicateAddressAlert, setIsShowDuplicateAddressAlert] = useState(false);

  const contactData = getContactData();

  const isDuplicateAddress = (existingAddresses: ICurrentAddressInfo[], newAddress: ICurrentAddressInfo): boolean => {
    return existingAddresses.some(
      (addr) =>
        addr.city === newAddress.city &&
        addr.state === newAddress.state &&
        addr.zip === newAddress.zip &&
        addr.streetLine1 === newAddress.streetLine1
    );
  };
  const totalMonthsAtAddress = (submittedaddresses: ICurrentAddressInfo[]) => {
    return submittedaddresses.reduce(
      (sum, address) =>
        sum +
        (address?.moveInDate === '' || address?.moveOutDate == '' ? 0 : monthsBetween(address?.moveInDate, address?.moveOutDate)),
      0
    );
  };

  const { errors, handleChange, handleSubmit, values, setValues, resetForm } = useForm<ICurrentAddressInfo>({
    initialValues: {
      streetLine1: '',
      streetLine2: '',
      city: '',
      state: '',
      zip: '',
      moveInDate: '',
      moveOutDate: '',
      type: '',
      monthlyPayment: 0,
    },
    validationSchema: {
      streetLine1: validate.string('streetLine1').required(t('addressHistory.formValidation.address')),
      streetLine2: validate.string('streetLine2'),
      city: validate.string('city').required(t('addressHistory.formValidation.city')),
      state: validate.string('state').required(t('addressHistory.formValidation.state')),
      zip: validate
        .string('zip')
        .required(t('addressHistory.formValidation.zipcode'))
        .matches(/^\d{5}$/, t('addressHistory.formValidation.validZipcode')),
      moveInDate: validate.string('moveInDate').required(t('addressHistory.formValidation.moveInDate')),
      moveOutDate:
        editingIndex === 0
          ? validate.string('moveOutDate')
          : validate.string('moveOutDate').required(t('addressHistory.formValidation.moveOutDate')),
      type: validate.string('type').required(t('addressHistory.formValidation.residentType')),
      monthlyPayment: validate.number('monthlyPayment'),
    },

    onSubmit: async (values) => {
      let updatedAddresses = [...addresses];

      // Check if  editing or adding a new address
      if (editingIndex !== null) {
        // Update existing address
        updatedAddresses[editingIndex] = addOrEditAddress(values);
        setEditingIndex(null);
        setIsShowDuplicateAddressAlert(false);
      } else {
        // Check for duplicate before adding new address
        if (isDuplicateAddress(addresses, values)) {
          setIsShowDuplicateAddressAlert(true);
          return;
        }

        // Add new address
        updatedAddresses = [...addresses, values];

        setIsShowDuplicateAddressAlert(false);
      }

      // Calculate current months
      const currentMonthsSubmitted = totalMonthsAtAddress(updatedAddresses);
      const remainingMonths = Math.max(0, REQUIRED_MONTHS - currentMonthsSubmitted);
      setnoOfMonths(remainingMonths > 0 ? remainingMonths : null);
      const trackingFormattedAddressResponse = [...updatedAddresses].map(({ zip, state, city }) => ({
        zip,
        state,
        city,
      }));
      // If met or exceeded the required months
      if (currentMonthsSubmitted >= REQUIRED_MONTHS) {
        // Submit full address history

        const addressHistoryPayload = [...updatedAddresses];

        if (addressHistory && addressHistory.length > 0) {
          // Add addresses from history only if they're not duplicates of any existing address
          addressHistory.forEach((historyAddress) => {
            const isDuplicate = isDuplicateAddress(addressHistoryPayload, historyAddress);

            if (!isDuplicate) {
              addressHistoryPayload.push(historyAddress);
            }
          });
        }
        submitStart();

        const createAddressHistoryResponse = await createAddressHistory(addressHistoryPayload, contactData?.contactId);

        if ('error' in createAddressHistoryResponse) {
          dispatch({
            type: CDX_ACTIONS.SUBMIT_API_ERROR,
            payload: createAddressHistoryResponse,
          });
        } else {
          submitSuccess();
          trackFormSubmit('Address Information Provided', {
            completed_status: true,
            submission_method: 'manual',
            addresses_provided_count: addressHistoryPayload.length,
            total_months_provided: currentMonthsSubmitted,
            months_at_address: monthsBetween(values?.moveInDate, values?.moveOutDate),
            months_of_history_required: 24,
            months_of_history_remaining: remainingMonths,
            form_context: 'address_loop',
            ...trackingFormattedAddressResponse,
          });
          trackFormSubmit('Form Submitted', {
            submission_status: true,
            submission_method: 'manual',
            addresses_provided_count: addressHistoryPayload.length,
            months_of_history_captured: currentMonthsSubmitted,
            months_of_history_required: 24,
            months_of_history_remaining: remainingMonths,
            forward_screen_name: 'employment_loop',
          });
          next(currentStep, 'postSubmitOutcome', {}, true);
        }
      } else {
        // Handle partial address history
        if (!addressHistory || addressHistory.length === 0) {
          setAddresses(updatedAddresses);
          return;
        }

        // The next address from history
        const nextAddressIndex = addressHistory.length - noOfNewAddressAddedFromHistory + 1;
        const nextAddress = addressHistory[nextAddressIndex];

        resetFormValues();

        if (noOfNewAddressAddedFromHistory < addressHistory.length && nextAddress && currentMonthsSubmitted < REQUIRED_MONTHS) {
          trackFormSubmit('Address Information Provided', {
            completed_status: false,
            submission_method: 'manual',
            addresses_provided_count: addressHistory.length,
            total_months_provided: currentMonthsSubmitted,
            months_at_address: monthsBetween(values?.moveInDate, values?.moveOutDate),
            months_of_history_required: 24,
            months_of_history_remaining: remainingMonths,
            form_context: 'address_loop',
            ...trackingFormattedAddressResponse,
          });
          setnoOfNewAddressAddedFromHistory((prev) => prev + 1);
          setAddresses([...updatedAddresses, nextAddress]);
        } else {
          setAddresses(updatedAddresses);
        }
      }
    },
    onValidationError: (errors, values) => {
      const { moveInDate } = values;
      const months = monthsBetween(moveInDate, new Date());
      trackFormSubmit('Form Submitted', {
        submission_status: false,
        submission_method: 'manual',
        addresses_provided_count: addresses,
        months_of_history_captured: Math.abs(noOfMonths ?? 0 + months),
        months_of_history_required: 24,
        months_of_history_remaining: Math.abs(24 - months),
        forward_screen_name: 'address_loop',
        submission_fail_reason: 'validation_error',
        validation_errors: convertArrayToSnakeCase(Object.keys(errors)),
      });
    },
  });

  useEffect(() => {
    const getinitAddress = () => {
      // Check if addressHistory is a valid array
      if (!Array.isArray(addressHistory)) {
        return [];
      }

      switch (addressHistory.length) {
        case 0:
          return [];
        case 1:
          return [addressHistory[0]];
        default: // 2 or more items
          return addressHistory.slice(0, 2);
      }
    };
    setAddresses(getinitAddress());
    setnoOfNewAddressAddedFromHistory(getinitAddress().length);
    const currentMonthsSubmitted = totalMonthsAtAddress(getinitAddress());
    const remainingMonths = Math.max(0, REQUIRED_MONTHS - currentMonthsSubmitted);

    setnoOfMonths(remainingMonths > 0 ? remainingMonths : null);
  }, [addressHistory]);

  const resetFormValues = () => {
    resetForm();
  };
  const handleEdit = (index: number, editingAddress: ICurrentAddressInfo) => {
    resetFormValues();
    setEditingIndex(index);
    setValues(editingAddress);
    setIsShowDuplicateAddressAlert(false);
  };

  const handleInputChange = <K extends keyof ICurrentAddressInfo>(field: K, value: ICurrentAddressInfo[K]) => {
    handleChange(field, value);
  };
  useEffectOnce(() => trackPage(t('pageTitles.AddressHistory')));
  return (
    <div>
      <h1 className="iLendingCDX-form-title">{t('addressHistory.title')}</h1>
      {addresses.length > 0 &&
        addresses.map((address, index) => (
          <div
            key={`${address?.streetLine1}${address?.city}${address?.state}${address?.zip}}${address?.moveInDate}`}
            className="iLendingCDX-verifyAddressCard iLendingCDX-mb-4"
          >
            <div className="iLendingCDX-verifyAddressCard-homeIcon ">
              <img
                src={AssetLinks.home}
                alt=""
              />
            </div>

            <div className="iLendingCDX-verifyAddressCard-info">
              <p>{address?.streetLine1} </p>
              <p>{address?.streetLine2}</p>
              <p>
                {address?.city}, {address?.state} {address?.zip}
              </p>
              <p>
                {address?.moveOutDate &&
                  address?.moveInDate &&
                  `${convertMonthsToYearsAndMonths(monthsBetween(address?.moveOutDate, address?.moveInDate))}`}
              </p>
            </div>
            <img
              src={AssetLinks.pencil}
              className="iLendingCDX-icon-button"
              alt="edit-icon"
              onClick={() => {
                handleEdit(index, {
                  ...addOrEditAddress(address),
                });
              }}
            />
          </div>
        ))}

      <h1 className="iLendingCDX-form-title2">{t('addressHistory.title2')}</h1>
      {noOfMonths && (
        <p className="iLendingCDX-remaining-months iLendingCDX-mb-4">
          {t('addressHistory.monthValidation.JustNeed')}
          <b>{noOfMonths}</b>
          {t('addressHistory.monthValidation.MoreMonths')}
        </p>
      )}
      {isShowDuplicateAddressAlert && <Alert message={t('addressHistory.duplicateAddressvalidation')} />}

      <form
        className={'iLendingCDX-mt-4'}
        onSubmit={handleSubmit}
        onFocus={startInteraction}
      >
        <TextInput
          label={t('addressHistory.form.address')}
          className={'iLendingCDX-mb-4'}
          name="address"
          value={values?.streetLine1}
          errorMessage={errors.streetLine1}
          onChange={(e) => handleInputChange('streetLine1', e.target.value)}
        />
        <TextInput
          label={t('addressHistory.form.APTSuiteNumber')}
          className={'iLendingCDX-mb-4'}
          name="APTSuiteNumber"
          value={values?.streetLine2}
          errorMessage={errors.streetLine2}
          onChange={(e) => handleInputChange('streetLine2', e.target.value)}
        />
        <TextInput
          label={t('addressHistory.form.city')}
          className={'iLendingCDX-mb-4'}
          name="city"
          value={values?.city}
          errorMessage={errors.city}
          onChange={(e) => handleInputChange('city', e.target.value)}
        />

        <CustomSelect
          label={t('addressHistory.form.state')}
          className={'iLendingCDX-mb-4'}
          name="state"
          options={countryStates}
          value={values?.state}
          errorMessage={errors.state}
          onChange={(e) => handleInputChange('state', e.target.value)}
        />
        <TextInput
          label={t('addressHistory.form.zipcode')}
          className={'iLendingCDX-mb-4'}
          name="zipcode"
          value={values?.zip}
          errorMessage={errors.zip}
          onChange={(e) => handleInputChange('zip', e.target.value)}
        />
        <TextInput
          label={t('addressHistory.form.moveInDate')}
          className={'iLendingCDX-mb-4'}
          name="moveInDate"
          type="date"
          value={values?.moveInDate}
          errorMessage={errors.moveInDate}
          max={todaysDate}
          onChange={(e) => handleInputChange('moveInDate', e.target.value as ICurrentAddressInfo['moveInDate'])}
        />
        {editingIndex === 0 ? null : (
          <TextInput
            label={t('addressHistory.form.moveOutDate')}
            className={'iLendingCDX-mb-4'}
            name="moveOutDate"
            type="date"
            value={values?.moveOutDate}
            errorMessage={errors.moveOutDate}
            max={todaysDate}
            onChange={(e) => handleInputChange('moveOutDate', e.target.value as ICurrentAddressInfo['moveOutDate'])}
          />
        )}

        <CustomSelect
          label={t('addressHistory.form.residentType')}
          className={'iLendingCDX-mb-4'}
          name="residentType"
          options={addressTypeOptions}
          value={values?.type}
          errorMessage={errors.type}
          onChange={(e) => handleInputChange('type', e.target.value)}
        />

        <PriceInput
          label={t('addressHistory.form.monthlyPaymentAmount')}
          className={'iLendingCDX-mb-8'}
          name="monthlyPayment"
          value={values?.monthlyPayment}
          errorMessage={errors.monthlyPayment}
          onChange={(value) => handleInputChange('monthlyPayment', value as ICurrentAddressInfo['monthlyPayment'])}
        />

        <Button
          type="submit"
          className="iLendingCDX-button--rounded"
          size="lg"
          isLoading={isLoading}
        >
          {t('enterLicensePlate.form.continue')}
        </Button>
      </form>
    </div>
  );
};
export default TwoYearsAddressHistoryForm;
